import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GrUpdate } from 'react-icons/gr';
import { useGetEtaiement, useGetEtaiementsList } from '~/hooks/etaiements';
import styles from './dimension-form.module.scss';

import { InputNumber, StyledSelect } from '~/lib/HooksFormFields';
import InputDate from '~/lib/HooksFormFields/InputDate';
import PreviewField from '~/components/PreviewField';
import { IPhase } from '~/types/estimates';
import { IOption } from '~/types/options';
import { digitFormatter } from '~/utils';
import computeFormworkPace from '~/utils/etaiement-dimension';
import computeRt50Tonnage from '~/utils/etaiement-dimension-tonnage';
import type { Rt50SlabThicknessOption } from '~/utils/etaiement-dimension-tonnage';

const formatFormworkStartDate = (value?: string) => {
  if (!value) return '---';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const DimensionForm = ({ isPreview }: { isPreview: boolean }) => {
  const { control, setValue, watch } = useFormContext();
  const { id, phaseId } = useParams();
  const { data: etaiement } = useGetEtaiement(id || '');
  const { data: list = { dimension: {} } } = useGetEtaiementsList();

  const rt50SlabThicknesses = useMemo<Rt50SlabThicknessOption[]>(
    () => list.dimension?.rt50SlabThicknesses || [],
    [list.dimension?.rt50SlabThicknesses],
  );
  const slabThicknessOptions: IOption[] = useMemo(
    () => rt50SlabThicknesses.map(({ label, value }) => ({ label, value })),
    [rt50SlabThicknesses],
  );

  const phase = useMemo(() => {
    const found = etaiement?.phases?.find((d: IPhase) => d._id === phaseId);
    return found;
  }, [etaiement, phaseId]);

  const length = watch('dimension.length');
  const width = watch('dimension.width');
  const scaffoldingSurface = watch('dimension.scaffoldingSurface');
  const slabThickness = watch('dimension.slabThickness');
  const totalWorksiteDurationMonths = watch('dimension.totalWorksiteDurationMonths');

  const formworkPace = useMemo(
    () => computeFormworkPace(scaffoldingSurface, totalWorksiteDurationMonths),
    [scaffoldingSurface, totalWorksiteDurationMonths],
  );

  const previewFormworkPace = useMemo(
    () => computeFormworkPace(
      phase?.dimension?.scaffoldingSurface,
      phase?.dimension?.totalWorksiteDurationMonths,
    ),
    [phase?.dimension?.scaffoldingSurface, phase?.dimension?.totalWorksiteDurationMonths],
  );

  useEffect(() => {
    if (isPreview) return;
    const tonnage = computeRt50Tonnage(scaffoldingSurface, slabThickness, rt50SlabThicknesses);
    if (tonnage != null) {
      setValue('dimension.tonnage', tonnage);
    }
  }, [isPreview, scaffoldingSurface, slabThickness, rt50SlabThicknesses, setValue]);

  const handleCalculateScaffoldingSurface = () => {
    if (length && width && !isPreview) {
      const surface = parseFloat(length) * parseFloat(width);
      setValue('dimension.scaffoldingSurface', surface.toFixed(2));
    }
  };

  const renderCalculatedFormworkPace = (pace: number | null) => (
    <div className={styles.indicativeField}>
      <span className={styles.indicativeLabel}>Cadence de coffrage calculée</span>
      <span className={styles.indicativeValue}>
        {pace != null ? `${digitFormatter?.format(pace)} m²/jour` : '---'}
      </span>
    </div>
  );

  return (
    <div id="dimension" className={styles.dimension}>
      <h4>Hypothèses chantier</h4>
      <div className={styles.row}>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Début du coffrage'
              value={formatFormworkStartDate(phase?.dimension?.formworkStartDate)}
            />
            : <InputDate
              name='dimension.formworkStartDate'
              label='Début du coffrage'
              control={control}
            />
          }
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Durée totale du chantier'
              value={
                phase?.dimension?.totalWorksiteDurationMonths != null
                  ? `${phase.dimension.totalWorksiteDurationMonths} mois`
                  : '---'
              }
            />
            : <InputNumber
              name='dimension.totalWorksiteDurationMonths'
              label='Durée totale du chantier'
              placeholder='Durée'
              unit='mois'
              allowFloat
              min='0'
              control={control}
            />
          }
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.containerField}>
          {renderCalculatedFormworkPace(isPreview ? previewFormworkPace : formworkPace)}
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Cadence de coffrage demandée'
              value={
                phase?.dimension?.requestedFormworkPace != null
                  ? `${digitFormatter?.format(phase.dimension.requestedFormworkPace)} m²/jour`
                  : '---'
              }
            />
            : <InputNumber
              name='dimension.requestedFormworkPace'
              label='Cadence de coffrage demandée'
              placeholder='Cadence'
              unit='m²/jour'
              allowFloat
              min='0'
              control={control}
            />
          }
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Longeur'
              value={phase?.dimension?.length ? `${phase?.dimension?.length} m` : '---'}
            />
            : <InputNumber
              name='dimension.length'
              label='Longeur'
              placeholder='en m'
              allowFloat={true}
              control={control}
            />
          }
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Largeur'
              value={phase?.dimension?.width ? `${phase?.dimension?.width} m` : '---'}
            />
            : <InputNumber
              name='dimension.width'
              label='Largeur'
              placeholder='en m'
              allowFloat={true}
              control={control}
            />
          }
        </div>
      </div>
      <div className={`${styles.row} ${styles.rowThree}`}>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Surface plancher'
              value={phase?.dimension?.scaffoldingSurface ? `${phase?.dimension?.scaffoldingSurface} m²` : '---'}
            />
            : <div className={styles.fieldWithButton}>
              <InputNumber
                name='dimension.scaffoldingSurface'
                label='Surface plancher'
                placeholder='en m2'
                control={control}
                allowFloat
              />
              <button
                type="button"
                className={styles.refreshButton}
                onClick={handleCalculateScaffoldingSurface}
                disabled={!length || !width}
                title="Calculer automatiquement la surface (Longueur × Largeur)"
              >
                {GrUpdate({})}
              </button>
            </div>
          }
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Épaisseur de dalle'
              value={
                slabThicknessOptions.find(
                  (option) => option.value === phase?.dimension?.slabThickness,
                )?.label
                || phase?.dimension?.slabThickness
                || '---'
              }
            />
            : <StyledSelect
              name='dimension.slabThickness'
              label='Épaisseur de dalle'
              placeholder='Sélectionner'
              control={control}
              options={slabThicknessOptions}
              isClearable
            />
          }
        </div>
        <div className={styles.containerField}>
          {isPreview
            ? <PreviewField
              label='Poids'
              value={phase?.dimension?.tonnage != null ? `${phase.dimension.tonnage} T` : '---'}
            />
            : <InputNumber
              name='dimension.tonnage'
              label='Poids'
              placeholder='en T'
              unit='T'
              control={control}
              allowFloat
              min='0'
            />
          }
        </div>
      </div>
    </div>
  );
};

export default DimensionForm;
